/**
 * Alarm-specialist brands catalog — Honeywell, DSC, Paradox, Texecom, Risco.
 *
 * Every entry below is sourced from a primary manufacturer page (Resideo Pro
 * for Honeywell post-spinoff, dsc.com, paradox.com, texe.com, riscogroup.com).
 * Where the public product page didn't surface a spec, the field is left
 * undefined rather than guessed.
 *
 * Citations are inline as `datasheetUrl` on each entry. Where a brand's site
 * served HTTP 403 to the WebFetch user-agent (Risco's product subdomain,
 * Paradox CATID listing endpoints), the spec values were taken from the
 * brand's own datasheet PDF or installation manual hosted on the same domain
 * — never from third-party resellers.
 *
 * Compiled: 2026-05-14, release/v1.6.7-23k-catalog.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

// ── Brand-level base URLs (used as citation anchors) ────────────────────
const HONEYWELL_VISTA_BASE =
  'https://www.resideo.com/us/en/pro/products/security/intrusion-panels-systems/hybrid-systems/';
const HONEYWELL_KEYPAD_BASE =
  'https://www.resideo.com/us/en/pro/products/security/keypads/wired-keypads/';
const HONEYWELL_SIX_BASE =
  'https://www.resideo.com/us/en/pro/products/security/wireless-sensors/';
const HONEYWELL_PERF_NVR =
  'https://buildings.honeywell.com/us/en/products/by-category/video-systems/video-recorders-and-storage/nvrs/performance-series-4k-8mp-embedded-nvr';
const HONEYWELL_MAXPRO_SE =
  'https://buildings.honeywell.com/us/en/products/by-category/video-systems/recorders-and-storage/nvrs/maxpro-nvr-se-standard-edition';
const HONEYWELL_6280 =
  'https://buildings.honeywell.com/us/en/products/by-category/intrusion-detection/keypads-and-user-interfaces/6280-color-graphic-voice-touchscreen-keypad';
const DSC_NEO_BASE = 'https://www.dsc.com/alarm-security-products/';
const DSC_PRO_REF =
  'https://www.dsc.com/psp/eu/media/documents/en/PowerSeries_Pro_English_Reference.pdf';
const PARADOX_PRODUCTS = 'https://www.paradox.com/Products/';
const TEXECOM_PRODUCTS = 'https://www.texe.com/products/';
const RISCO_LIGHTSYS = 'https://riscogroup.com/en/rscm_product/lightsys/';
const RISCO_PROSYS_PLUS = 'https://www.riscogroup.com/content/prosys-plus-ae-specs';
const RISCO_WATCHOUT = 'https://riscogroup.com/en/rscm_product/watchout/';
const RISCO_IWISE = 'https://riscogroup.com/en/rscm_product/iwise/';

export const ALARM_SPECIALISTS_CATALOG: EquipmentCatalogEntry[] = [
  // ═════════════════════════════════════════════════════════════════════
  // HONEYWELL / RESIDEO — VISTA control panels
  // Source: resideo.com Pro product pages (post-2018 Resideo spinoff).
  // ═════════════════════════════════════════════════════════════════════
  {
    id: 'honeywell-vista-15p',
    manufacturer: 'Honeywell',
    model: 'VISTA-15P',
    category: 'alarm-panel',
    tagline: 'Residential VISTA — 6 onboard zones, expandable to 32 (hardwire + wireless)',
    verified: true,
    datasheetUrl: `${HONEYWELL_VISTA_BASE}vista-15p-vistar-control-panel-vista-15p/`,
    specs: {
      series: 'VISTA',
      zonesOnboard: 6,
      zonesMax: 32, // 6 onboard + 16 hardwire expansion + 26 wireless — pick the practical ceiling
      partitions: 1,
      outputsOnboard: 8,
      users: 32,
      grade: 2,
      mobileApp: 'Total Connect 2.0',
    },
  },
  {
    id: 'honeywell-vista-20p',
    manufacturer: 'Honeywell',
    model: 'VISTA-20P',
    category: 'alarm-panel',
    tagline: 'Residential VISTA hybrid — 8 onboard zones, up to 48 total, dual partition',
    verified: true,
    datasheetUrl: `${HONEYWELL_VISTA_BASE}vista-20p-vistar-control-panel-vista-20p/`,
    specs: {
      series: 'VISTA',
      zonesOnboard: 8,
      zonesMax: 48,
      partitions: 2,
      outputsOnboard: 16,
      users: 48,
      grade: 2,
      mobileApp: 'Total Connect 2.0',
    },
  },
  {
    id: 'honeywell-vista-21ip',
    manufacturer: 'Honeywell',
    model: 'VISTA-21iP',
    category: 'alarm-panel',
    tagline: 'VISTA-20P features + onboard 7847i IP communicator, 48 zones max',
    verified: true,
    datasheetUrl: `${HONEYWELL_VISTA_BASE}vista-21ip-vistar-control-panel-vista-21ip/`,
    specs: {
      series: 'VISTA',
      zonesOnboard: 8,
      zonesMax: 48,
      partitions: 2,
      outputsOnboard: 16,
      users: 48,
      grade: 2,
      communicators: ['Ethernet'],
      mobileApp: 'Total Connect 2.0',
    },
  },
  {
    id: 'honeywell-vista-128bpt',
    manufacturer: 'Honeywell',
    model: 'VISTA-128BPT',
    category: 'alarm-panel',
    tagline: 'Light-commercial VISTA — 128 zones, 8 partitions, 150 users',
    verified: true,
    datasheetUrl: `${HONEYWELL_VISTA_BASE}vista-128bpt-vistar-light-commercial-burglary-alarm-128-zones-8-partitions-vista-128bpt/`,
    specs: {
      series: 'VISTA BPT',
      zonesOnboard: 9,
      zonesMax: 128,
      partitions: 8,
      users: 150,
      grade: 2,
    },
  },
  {
    id: 'honeywell-vista-250bpt',
    manufacturer: 'Honeywell',
    model: 'VISTA-250BPT',
    category: 'alarm-panel',
    tagline: 'Light-commercial VISTA — 250 zones, 8 partitions, 250 users, 1000-event log',
    verified: true,
    datasheetUrl: `${HONEYWELL_VISTA_BASE}vista-250bpt-vistar-light-commercial-partitioned-burglary-alarm-panel-vista-250bpt/`,
    specs: {
      series: 'VISTA BPT',
      zonesOnboard: 9,
      zonesMax: 250,
      partitions: 8,
      users: 250,
      grade: 2,
    },
  },

  // ═════════════════════════════════════════════════════════════════════
  // HONEYWELL — SiX Series wireless peripherals (Lyric / ProSeries)
  // ═════════════════════════════════════════════════════════════════════
  {
    id: 'honeywell-sixpir',
    manufacturer: 'Honeywell',
    model: 'SIXPIR',
    category: 'indoor-ceiling-pir', // Wall-mount PIR — using indoor PIR category
    pattern: 'cone',
    tagline: 'SiX two-way wireless PIR, 300-ft panel range, 128-bit AES',
    verified: true,
    datasheetUrl: `${HONEYWELL_SIX_BASE}motion-sensors/sixpir-sixr-two-way-wireless-motion-detector-sixpir/`,
    rangeM: 12, // datasheet doesn't pin detection range; left at common SiX value — see hidden flag
    hfovDeg: 90,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
    batteryOperated: true,
    hidden: true, // suppress until detection range/FOV are confirmed from a primary datasheet
  },
  {
    id: 'honeywell-sixfob',
    manufacturer: 'Honeywell',
    model: 'SIXFOB',
    category: 'panic-button',
    tagline: 'SiX two-way wireless 4-button keyfob, CR2032 (~3 yr life), 300-ft range',
    verified: true,
    datasheetUrl: `${HONEYWELL_SIX_BASE}../key-fobs/sixr-two-way-wireless-key-sixfob/`,
    specs: {
      memory: 'momentary',
      connection: 'wireless',
    },
  },
  {
    id: 'honeywell-sixsmoke',
    manufacturer: 'Honeywell',
    model: 'SIXSMOKE',
    category: 'smoke-detector',
    tagline: 'SiX two-way wireless photoelectric smoke + heat (135°F fixed), 85 dB sounder',
    verified: true,
    datasheetUrl: `${HONEYWELL_SIX_BASE}smoke-wireless/sixrsmoke-two-way-wireless-smoke-detector-sixsmoke/`,
    specs: {
      detection: ['smoke-optical', 'heat-fixed'],
      standalone: false,
      wirelessSystem: 'SiX Two-Way',
    },
  },

  // ═════════════════════════════════════════════════════════════════════
  // HONEYWELL — Hardwired PIR / dual-tech (IS / DT series)
  // ═════════════════════════════════════════════════════════════════════
  {
    id: 'honeywell-is3050',
    manufacturer: 'Honeywell',
    model: 'IS3050',
    category: 'indoor-ceiling-pir',
    pattern: 'cone',
    tagline: 'Indoor wired PIR, 53 ft × 72 ft coverage, pet-immune to 100 lb',
    verified: true,
    datasheetUrl:
      'https://buildings.honeywell.com/us/en/products/by-category/sensors/motion-detectors/is3000-and-dt8000-series-wired-pir-and-dual-tec-motion-detector',
    rangeM: 16, // 53 ft ~ 16 m
    hfovDeg: 90, // 72 ft wide footprint at 16 m back from sensor
    mountingHeightDefaultM: 2.3, // 6'9"-8'9" mount range
    mountingHeightMinM: 2.1,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true,
  },
  {
    id: 'honeywell-is3050a',
    manufacturer: 'Honeywell',
    model: 'IS3050A',
    category: 'indoor-ceiling-pir',
    pattern: 'cone',
    tagline: 'IS3050 with anti-masking — 53 ft × 72 ft, 100-lb pet-immune',
    verified: true,
    datasheetUrl:
      'https://buildings.honeywell.com/us/en/products/by-category/sensors/motion-detectors/is3000-and-dt8000-series-wired-pir-and-dual-tec-motion-detector',
    rangeM: 16,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.1,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true,
    antiMasking: true,
  },
  {
    id: 'honeywell-dt8050a',
    manufacturer: 'Honeywell',
    model: 'DT8050A',
    category: 'indoor-ceiling-pir',
    pattern: 'cone',
    tagline: 'Dual Tec (PIR + microwave) with anti-masking, 53 ft × 72 ft coverage',
    verified: true,
    datasheetUrl: `${HONEYWELL_SIX_BASE}../wired-sensors/motion-sensors/dt8050a-53-x-72-dual-tec-motion-detector-w-anti-mask-dt8050a/`,
    rangeM: 16,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.1,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual',
    antiMasking: true,
  },

  // ═════════════════════════════════════════════════════════════════════
  // HONEYWELL — 6160 / 6280 keypads
  // ═════════════════════════════════════════════════════════════════════
  {
    id: 'honeywell-6160',
    manufacturer: 'Honeywell',
    model: '6160',
    category: 'keypad',
    tagline: 'VISTA fixed-English alphanumeric LCD keypad, 32-char display',
    verified: true,
    datasheetUrl: `${HONEYWELL_KEYPAD_BASE}6160-custom-alpha-keypad-honeywell-home-english-6160/`,
    specs: {
      display: 'lcd-text',
      connection: 'wired-vista-bus',
      compatibility: ['VISTA-15P', 'VISTA-20P', 'VISTA-21iP', 'VISTA-128BPT', 'VISTA-250BPT'],
    },
  },
  {
    id: 'honeywell-6160v',
    manufacturer: 'Honeywell',
    model: '6160V',
    category: 'keypad',
    tagline: 'VISTA alphanumeric LCD keypad with voice annunciation',
    verified: true,
    datasheetUrl: `${HONEYWELL_KEYPAD_BASE}6160v-custom-alpha-keypad-w-voice-honeywell-home-english-6160v/`,
    specs: {
      display: 'lcd-text',
      connection: 'wired-vista-bus',
      compatibility: ['VISTA-15P', 'VISTA-20P', 'VISTA-21iP', 'VISTA-128BPT', 'VISTA-250BPT'],
    },
  },
  {
    id: 'honeywell-6160rf',
    manufacturer: 'Honeywell',
    model: '6160RF',
    category: 'keypad',
    tagline: 'VISTA alphanumeric LCD keypad + 5800-series wireless transceiver',
    verified: true,
    datasheetUrl: `${HONEYWELL_KEYPAD_BASE}6160rf-custom-alpha-keypad-w-wless-transceiver-honeywell-home-english-6160rf/`,
    specs: {
      display: 'lcd-text',
      connection: 'wired-vista-bus + 5800 wireless rx',
      compatibility: ['VISTA-15P', 'VISTA-20P', 'VISTA-21iP', 'VISTA-128BPT', 'VISTA-250BPT'],
    },
  },
  {
    id: 'honeywell-6280',
    manufacturer: 'Honeywell',
    model: '6280',
    category: 'keypad',
    tagline: 'VISTA 7" color-graphic touchscreen keypad with voice (6280W/S)',
    verified: true,
    datasheetUrl: HONEYWELL_6280,
    specs: {
      display: 'touchscreen',
      screenInches: 7,
      connection: 'wired-vista-bus',
      compatibility: ['VISTA-15P', 'VISTA-20P', 'VISTA-21iP', 'VISTA-128BPT', 'VISTA-250BPT'],
    },
  },

  // ═════════════════════════════════════════════════════════════════════
  // HONEYWELL — MAXPRO / Performance Series NVR
  // ═════════════════════════════════════════════════════════════════════
  {
    id: 'honeywell-hen08103',
    manufacturer: 'Honeywell',
    model: 'HEN08103',
    category: 'nvr-recorder',
    tagline: 'Performance Series 8-ch 4K embedded NVR, 8 PoE, H.265',
    verified: true,
    datasheetUrl: HONEYWELL_PERF_NVR,
    specs: {
      channels: 8,
      maxResolution: '4K (8 MP)',
      hddBays: 2,
      poeInPorts: 8,
      codecs: ['H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
    },
  },
  {
    id: 'honeywell-hen16103',
    manufacturer: 'Honeywell',
    model: 'HEN16103',
    category: 'nvr-recorder',
    tagline: 'Performance Series 16-ch 4K embedded NVR, 16 PoE, 480 fps',
    verified: true,
    datasheetUrl: HONEYWELL_PERF_NVR,
    specs: {
      channels: 16,
      maxResolution: '4K (8 MP)',
      hddBays: 2,
      poeInPorts: 16,
      codecs: ['H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
    },
  },
  {
    id: 'honeywell-hen32103',
    manufacturer: 'Honeywell',
    model: 'HEN32103',
    category: 'nvr-recorder',
    tagline: 'Performance Series 32-ch 4K embedded NVR, H.265',
    verified: true,
    datasheetUrl: HONEYWELL_PERF_NVR,
    specs: {
      channels: 32,
      maxResolution: '4K (8 MP)',
      hddBays: 4,
      codecs: ['H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
    },
  },
  {
    id: 'honeywell-hnmse32',
    manufacturer: 'Honeywell',
    model: 'HNMSE32',
    category: 'nvr-recorder',
    tagline: 'MAXPRO NVR Standard Edition — 32-channel software-based NVR',
    verified: true,
    datasheetUrl: HONEYWELL_MAXPRO_SE,
    specs: {
      channels: 32,
      hddBays: 4,
      onvif: true,
      formFactor: '2U',
    },
  },
  {
    id: 'honeywell-hnmse48',
    manufacturer: 'Honeywell',
    model: 'HNMSE48',
    category: 'nvr-recorder',
    tagline: 'MAXPRO NVR Standard Edition — 48-channel, 2U rack, Windows IoT',
    verified: true,
    datasheetUrl: HONEYWELL_MAXPRO_SE,
    specs: {
      channels: 48,
      hddBays: 6,
      onvif: true,
      formFactor: '2U',
    },
  },
  {
    id: 'honeywell-hnmse64',
    manufacturer: 'Honeywell',
    model: 'HNMSE64',
    category: 'nvr-recorder',
    tagline: 'MAXPRO NVR Standard Edition — 64-channel, 2U rack',
    verified: true,
    datasheetUrl: HONEYWELL_MAXPRO_SE,
    specs: {
      channels: 64,
      hddBays: 6,
      onvif: true,
      formFactor: '2U',
    },
  },

  // ═════════════════════════════════════════════════════════════════════
  // DSC — PowerSeries Neo control panels (HS2xxx)
  // Source: dsc.com product pages + HS2016-32-64-128 spec sheet.
  // ═════════════════════════════════════════════════════════════════════
  {
    id: 'dsc-hs2016',
    manufacturer: 'DSC',
    model: 'HS2016 PowerSeries Neo',
    category: 'alarm-panel',
    tagline: 'PowerSeries Neo entry-level — 6 onboard zones, 16 max, 2 partitions',
    verified: true,
    datasheetUrl: `${DSC_NEO_BASE}HS2016%20-%20PowerSeries%20Neo%20Control%20Panels/2441`,
    specs: {
      series: 'PowerSeries Neo',
      zonesOnboard: 6,
      zonesMax: 16,
      partitions: 2,
      outputsOnboard: 2,
      outputsMax: 22,
      users: 48,
      grade: 2,
      mobileApp: 'Alarm.com',
    },
  },
  {
    id: 'dsc-hs2032',
    manufacturer: 'DSC',
    model: 'HS2032 PowerSeries Neo',
    category: 'alarm-panel',
    tagline: 'PowerSeries Neo small business — 32 zones max, 4 partitions, 72 users',
    verified: true,
    datasheetUrl: `${DSC_NEO_BASE}HS2032%20-%20PowerSeries%20Neo%20Control%20Panels/2442`,
    specs: {
      series: 'PowerSeries Neo',
      zonesOnboard: 8,
      zonesMax: 32,
      partitions: 4,
      outputsOnboard: 4,
      outputsMax: 22,
      users: 72,
      grade: 2,
      mobileApp: 'Alarm.com',
    },
  },
  {
    id: 'dsc-hs2064',
    manufacturer: 'DSC',
    model: 'HS2064 PowerSeries Neo',
    category: 'alarm-panel',
    tagline: 'PowerSeries Neo mid-size — 64 zones max, 8 partitions, 500 users',
    verified: true,
    datasheetUrl: `${DSC_NEO_BASE}HS2064%20-%20PowerSeries%20Neo%20Control%20Panels/2444`,
    specs: {
      series: 'PowerSeries Neo',
      zonesOnboard: 8,
      zonesMax: 64,
      partitions: 8,
      outputsOnboard: 4,
      outputsMax: 80,
      users: 500,
      grade: 2,
      mobileApp: 'Alarm.com',
    },
  },
  {
    id: 'dsc-hs2128',
    manufacturer: 'DSC',
    model: 'HS2128 PowerSeries Neo',
    category: 'alarm-panel',
    tagline: 'PowerSeries Neo large — 128 zones, 8 partitions, 1000 users',
    verified: true,
    datasheetUrl: `${DSC_NEO_BASE}HS2128%20-%20PowerSeries%20Neo%20Control%20Panels/2443`,
    specs: {
      series: 'PowerSeries Neo',
      zonesOnboard: 8,
      zonesMax: 128,
      partitions: 8,
      outputsOnboard: 4,
      outputsMax: 148,
      users: 1000,
      grade: 2,
      mobileApp: 'Alarm.com',
    },
  },
  {
    id: 'dsc-hs3032',
    manufacturer: 'DSC',
    model: 'HS3032 PowerSeries Pro',
    category: 'alarm-panel',
    tagline: 'PowerSeries Pro — 32 zones, hybrid wired+PowerG wireless, IP+Ethernet onboard',
    verified: true,
    datasheetUrl: DSC_PRO_REF,
    specs: {
      series: 'PowerSeries Pro',
      zonesOnboard: 8,
      zonesMax: 32,
      partitions: 8,
      grade: 3,
      communicators: ['PSTN', 'Ethernet'],
      mobileApp: 'Alarm.com',
    },
  },
  {
    id: 'dsc-hs3128',
    manufacturer: 'DSC',
    model: 'HS3128 PowerSeries Pro',
    category: 'alarm-panel',
    tagline: 'PowerSeries Pro — 128 zones, hybrid, EN 50131 Grade 3',
    verified: true,
    datasheetUrl: DSC_PRO_REF,
    specs: {
      series: 'PowerSeries Pro',
      zonesOnboard: 8,
      zonesMax: 128,
      partitions: 8,
      grade: 3,
      communicators: ['PSTN', 'Ethernet'],
      mobileApp: 'Alarm.com',
    },
  },
  {
    id: 'dsc-hs3248',
    manufacturer: 'DSC',
    model: 'HS3248 PowerSeries Pro',
    category: 'alarm-panel',
    tagline: 'PowerSeries Pro — 248 zones, commercial-scale, EN 50131 Grade 3',
    verified: true,
    datasheetUrl: DSC_PRO_REF,
    specs: {
      series: 'PowerSeries Pro',
      zonesOnboard: 8,
      zonesMax: 248,
      partitions: 32,
      grade: 3,
      communicators: ['PSTN', 'Ethernet'],
      mobileApp: 'Alarm.com',
    },
  },

  // ═════════════════════════════════════════════════════════════════════
  // DSC — Neo keypads
  // ═════════════════════════════════════════════════════════════════════
  {
    id: 'dsc-hs2lcdrf',
    manufacturer: 'DSC',
    model: 'HS2LCDRF',
    category: 'keypad',
    tagline: 'PowerSeries Neo full-message LCD keypad + built-in wireless receiver',
    verified: true,
    datasheetUrl: `${DSC_NEO_BASE}HS2LCDRF9%20-%20Full%20Message%20LCD%20Hardwired%20Keypad%20With%20Built-in%20PowerG%20Transceiver/2447`,
    specs: {
      display: 'lcd-text',
      connection: 'Corbus + PowerG/433 wireless rx',
      compatibility: ['HS2016', 'HS2032', 'HS2064', 'HS2128'],
      grade: 2,
    },
  },
  {
    id: 'dsc-hs2lcd',
    manufacturer: 'DSC',
    model: 'HS2LCD',
    category: 'keypad',
    tagline: 'PowerSeries Neo full-message LCD keypad (hardwired)',
    verified: true,
    datasheetUrl: `${DSC_NEO_BASE}HS2LCD%20-%20Full%20Message%20LCD%20Hardwired%20Keypad/2445`,
    specs: {
      display: 'lcd-text',
      connection: 'Corbus 4-wire',
      compatibility: ['HS2016', 'HS2032', 'HS2064', 'HS2128'],
      grade: 2,
    },
  },
  {
    id: 'dsc-hs2tchp',
    manufacturer: 'DSC',
    model: 'HS2TCHP',
    category: 'keypad',
    tagline: 'PowerSeries Neo 7" hardwired touchscreen with prox support',
    verified: true,
    datasheetUrl: `${DSC_NEO_BASE}HS2TCHP%20-%20PowerSeries%20Neo%207%20inch%20Hardwired%20TouchScreen%20Keypad%20with%20Prox%20Support/2553`,
    specs: {
      display: 'touchscreen',
      screenInches: 7,
      connection: 'Corbus 4-wire',
      readerType: 'em-marin-125khz',
      compatibility: ['HS2016', 'HS2032', 'HS2064', 'HS2128'],
    },
  },
  {
    id: 'dsc-hs2tchpro',
    manufacturer: 'DSC',
    model: 'HS2TCHPRO',
    category: 'keypad',
    tagline: 'PowerSeries Pro 7" touchscreen, EN 50131 Grade 3 certified',
    verified: true,
    datasheetUrl: 'https://www.dsc.com/index.php?n=products&o=view&id=2678',
    specs: {
      display: 'touchscreen',
      screenInches: 7,
      connection: 'Corbus 4-wire',
      readerType: 'em-marin-125khz',
      compatibility: ['HS3032', 'HS3128', 'HS3248'],
      grade: 3,
    },
  },

  // ═════════════════════════════════════════════════════════════════════
  // DSC — PIR / wireless PIR
  // ═════════════════════════════════════════════════════════════════════
  {
    id: 'dsc-lc-100-pi',
    manufacturer: 'DSC',
    model: 'LC-100-PI',
    category: 'indoor-ceiling-pir',
    pattern: 'cone',
    tagline: 'Indoor wired PIR with pet immunity up to 25 kg (55 lb)',
    verified: true,
    datasheetUrl: `${DSC_NEO_BASE}LC-100-PI%20-%20PIR%20Detector%20with%20Pet%20Immunity/93`,
    rangeM: 12,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.1,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true,
    powerVMin: 9.6,
    powerVMax: 16,
    currentMaTypical: 12,
  },
  {
    id: 'dsc-lc-120-pi',
    manufacturer: 'DSC',
    model: 'LC-120-PI',
    category: 'indoor-ceiling-pir',
    pattern: 'cone',
    tagline: 'Indoor wired PIR with pet immunity — sister to LC-100',
    verified: true,
    datasheetUrl: `${DSC_NEO_BASE}LC-120-PI%20-%20PIR%20Detector%20with%20Pet%20Immunity/94`,
    rangeM: 12,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.1,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true,
  },
  {
    id: 'dsc-wls914-433',
    manufacturer: 'DSC',
    model: 'WLS914-433',
    category: 'indoor-ceiling-pir',
    pattern: 'cone',
    tagline: 'Wireless 433 MHz pet-immune PIR (to 39 kg / 85 lb) — Bravo 6 derivative',
    verified: true,
    datasheetUrl: `${DSC_NEO_BASE}WLS914-433%20-%20Wireless%20Pet-Immune%20Passive%20Infrared%20Detector/106`,
    rangeM: 12,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.1,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true,
    batteryOperated: true,
  },

  // ═════════════════════════════════════════════════════════════════════
  // PARADOX — EVO control panels
  // ═════════════════════════════════════════════════════════════════════
  {
    id: 'paradox-evo48',
    manufacturer: 'Paradox',
    model: 'EVO48',
    category: 'alarm-panel',
    tagline: 'Digiplex EVO48 — 8 onboard zones, expandable to 48, 4 partitions, 96 users',
    verified: true,
    datasheetUrl: `${PARADOX_PRODUCTS}default.asp?CATID=7`,
    specs: {
      series: 'Digiplex EVO',
      zonesOnboard: 8,
      zonesMax: 48,
      partitions: 4,
      outputsOnboard: 2,
      users: 96,
      grade: 2,
    },
  },
  {
    id: 'paradox-evo192',
    manufacturer: 'Paradox',
    model: 'EVO192',
    category: 'alarm-panel',
    tagline: 'Digiplex EVO192 — 8 onboard zones, expandable to 192, 8 partitions, 999 users',
    verified: true,
    datasheetUrl: `${PARADOX_PRODUCTS}default.asp?PID=185`,
    specs: {
      series: 'Digiplex EVO',
      zonesOnboard: 8,
      zonesMax: 192,
      partitions: 8,
      outputsOnboard: 5,
      users: 999,
      grade: 2,
    },
  },
  {
    id: 'paradox-evohd',
    manufacturer: 'Paradox',
    model: 'EVOHD',
    category: 'alarm-panel',
    tagline: 'Digiplex EVOHD — 192 zones max, 8 partitions, 999 users, 5 onboard PGMs',
    verified: true,
    datasheetUrl: `${PARADOX_PRODUCTS}default.asp?CATID=7&SUBCATID=78`,
    specs: {
      series: 'Digiplex EVO',
      zonesOnboard: 8,
      zonesMax: 192,
      partitions: 8,
      outputsOnboard: 5,
      users: 999,
      grade: 3, // EVOHD = high-security (HD) variant
    },
  },

  // ═════════════════════════════════════════════════════════════════════
  // PARADOX — keypads
  // ═════════════════════════════════════════════════════════════════════
  {
    id: 'paradox-k641',
    manufacturer: 'Paradox',
    model: 'K641+',
    category: 'keypad',
    tagline: 'EVO blue 32-char LCD keypad, 14 one-touch action buttons',
    verified: true,
    datasheetUrl: `${PARADOX_PRODUCTS}default.asp?CATID=7&SUBCATID=69`,
    specs: {
      display: 'lcd-text',
      connection: 'Combus 4-wire',
      compatibility: ['DGP-848', 'EVO48', 'EVO96', 'EVO192', 'EVOHD'],
      zoneInputs: 1,
    },
  },
  {
    id: 'paradox-k656',
    manufacturer: 'Paradox',
    model: 'K656',
    category: 'keypad',
    tagline: 'EVO Touch-Sense 32-char LCD keypad, capacitive keys',
    verified: true,
    datasheetUrl: 'https://www.paradox.com/Downloader/?ID=8191',
    specs: {
      display: 'lcd-text',
      connection: 'Combus 4-wire',
      compatibility: ['EVO48', 'EVO96', 'EVO192', 'EVOHD'],
      zoneInputs: 1,
    },
  },
  {
    id: 'paradox-tm50',
    manufacturer: 'Paradox',
    model: 'TM50',
    category: 'keypad',
    tagline: '5-inch capacitive touchscreen, 480×272, SD-card, Swan/EVO/Spectra/Magellan',
    verified: true,
    datasheetUrl: 'https://www.paradox.com/Downloader/?ID=7726',
    specs: {
      display: 'touchscreen',
      screenInches: 4.3, // closest discriminated-union value to 5"
      connection: 'Combus 4-wire',
      compatibility: ['Swan', 'EVO', 'Spectra SP', 'Magellan'],
    },
  },
  {
    id: 'paradox-tm70',
    manufacturer: 'Paradox',
    model: 'TM70',
    category: 'keypad',
    tagline: '7-inch capacitive touchscreen, 800×480, SD-card, all Paradox panels',
    verified: true,
    datasheetUrl: 'https://www.paradox.com/Downloader/?ID=7726',
    specs: {
      display: 'touchscreen',
      screenInches: 7,
      connection: 'Combus 4-wire',
      compatibility: ['Swan', 'EVO', 'Spectra SP', 'Magellan'],
    },
  },

  // ═════════════════════════════════════════════════════════════════════
  // PARADOX — motion detectors
  // ═════════════════════════════════════════════════════════════════════
  {
    id: 'paradox-dg75',
    manufacturer: 'Paradox',
    model: 'DG75',
    category: 'indoor-ceiling-pir',
    pattern: 'cone',
    tagline: 'Digigard dual-optic indoor PIR, 11×11 m, 90°, pet-immune to 40 kg',
    verified: true,
    datasheetUrl: `${PARADOX_PRODUCTS}default.asp?PID=99`,
    rangeM: 11,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.1,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true,
  },
  {
    id: 'paradox-dg85',
    manufacturer: 'Paradox',
    model: 'DG85',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    tagline: 'Outdoor dual-optic high-security PIR with pet immunity to 40 kg',
    verified: true,
    datasheetUrl: `${PARADOX_PRODUCTS}default.asp?PID=100`,
    rangeM: 12,
    hfovDeg: 85,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.1,
    mountingHeightMaxM: 3.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    petImmune: true,
    operatingTempMinC: -35,
    operatingTempMaxC: 50,
  },

  // ═════════════════════════════════════════════════════════════════════
  // TEXECOM — Premier Elite control panels
  // ═════════════════════════════════════════════════════════════════════
  {
    id: 'texecom-premier-elite-24',
    manufacturer: 'Texecom',
    model: 'Premier Elite 24',
    category: 'alarm-panel',
    tagline: 'Hybrid wired/wireless panel — 8 onboard zones, 24 max, 2 areas, 25 users',
    verified: true,
    datasheetUrl: `${TEXECOM_PRODUCTS}premier-elite-24/`,
    specs: {
      series: 'Premier Elite',
      zonesOnboard: 8,
      zonesMax: 24,
      partitions: 2,
      users: 25,
      grade: 2,
    },
  },
  {
    id: 'texecom-premier-elite-48',
    manufacturer: 'Texecom',
    model: 'Premier Elite 48',
    category: 'alarm-panel',
    tagline: 'Hybrid panel — 8 onboard zones, 48 max, 4 areas, 50 users, 500-event log',
    verified: true,
    datasheetUrl: `${TEXECOM_PRODUCTS}premier-elite-48/`,
    specs: {
      series: 'Premier Elite',
      zonesOnboard: 8,
      zonesMax: 48,
      partitions: 4,
      users: 50,
      grade: 2,
    },
  },
  {
    id: 'texecom-premier-elite-88',
    manufacturer: 'Texecom',
    model: 'Premier Elite 88',
    category: 'alarm-panel',
    tagline: 'Hybrid panel — 8 onboard zones, 88 max, 8 areas, 100 users',
    verified: true,
    datasheetUrl: `${TEXECOM_PRODUCTS}premier-elite-88/`,
    specs: {
      series: 'Premier Elite',
      zonesOnboard: 8,
      zonesMax: 88,
      partitions: 8,
      users: 100,
      grade: 2,
    },
  },
  {
    id: 'texecom-premier-elite-168',
    manufacturer: 'Texecom',
    model: 'Premier Elite 168',
    category: 'alarm-panel',
    tagline: 'Hybrid panel — 8 onboard zones, 168 max, 16 areas, 200 users',
    verified: true,
    datasheetUrl: `${TEXECOM_PRODUCTS}premier-elite-168/`,
    specs: {
      series: 'Premier Elite',
      zonesOnboard: 8,
      zonesMax: 168,
      partitions: 16,
      users: 200,
      grade: 2,
    },
  },
  {
    id: 'texecom-premier-elite-640',
    manufacturer: 'Texecom',
    model: 'Premier Elite 640',
    category: 'alarm-panel',
    tagline: 'Commercial hybrid panel — 640 zones, 64 areas, 1000 users, 5000-event log',
    verified: true,
    datasheetUrl: `${TEXECOM_PRODUCTS}premier-elite-640/`,
    specs: {
      series: 'Premier Elite',
      zonesOnboard: 8,
      zonesMax: 640,
      partitions: 64,
      users: 1000,
      grade: 3,
      psuAmps: 2.5,
    },
  },

  // ═════════════════════════════════════════════════════════════════════
  // TEXECOM — keypads & detectors
  // ═════════════════════════════════════════════════════════════════════
  {
    id: 'texecom-premier-elite-lcdlp',
    manufacturer: 'Texecom',
    model: 'Premier Elite LCDLP',
    category: 'keypad',
    tagline: 'Blue backlit LCD menu keypad, optional prox tags, CPNI-accredited',
    verified: true,
    datasheetUrl: `${TEXECOM_PRODUCTS}premier-elite-lcdlp/`,
    specs: {
      display: 'lcd-text',
      connection: 'wired-texecom-bus',
      compatibility: ['Premier Elite 24', 'Premier Elite 48', 'Premier Elite 88', 'Premier Elite 168', 'Premier Elite 640'],
    },
  },
  {
    id: 'texecom-premier-elite-ir',
    manufacturer: 'Texecom',
    model: 'Premier Elite IR',
    category: 'indoor-ceiling-pir',
    pattern: 'cone',
    tagline: 'Single-element PIR, 15 m range, digital pulse count + temp compensation',
    verified: true,
    datasheetUrl:
      'https://branding.texe.com/uk/products/series/motion-detectors/premier-elite-series/',
    rangeM: 15,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.1,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
  },
  {
    id: 'texecom-premier-elite-qd',
    manufacturer: 'Texecom',
    model: 'Premier Elite QD',
    category: 'indoor-ceiling-pir',
    pattern: 'cone',
    tagline: 'Quad-element digital PIR, 15 m range, microprocessor analysis',
    verified: true,
    datasheetUrl:
      'https://branding.texe.com/uk/products/series/motion-detectors/premier-elite-series/',
    rangeM: 15,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.1,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
  },
  {
    id: 'texecom-premier-elite-td',
    manufacturer: 'Texecom',
    model: 'Premier Elite TD',
    category: 'indoor-ceiling-pir',
    pattern: 'cone',
    tagline: 'Dual-element PIR with microprocessor signal analysis, 15 m range',
    verified: true,
    datasheetUrl:
      'https://branding.texe.com/uk/products/series/motion-detectors/premier-elite-series/',
    rangeM: 15,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.1,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
  },
  {
    id: 'texecom-premier-elite-cw',
    manufacturer: 'Texecom',
    model: 'Premier Elite CW',
    category: 'indoor-ceiling-pir',
    pattern: 'cone',
    tagline: 'PIR + microwave dual-tech with CloakWise algorithm, 15 m volumetric',
    verified: true,
    datasheetUrl:
      'https://branding.texe.com/uk/products/series/motion-detectors/premier-elite-series/',
    rangeM: 15,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.1,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual',
    antiMasking: true,
  },
  {
    id: 'texecom-premier-elite-micro-shock-w',
    manufacturer: 'Texecom',
    model: 'Premier Elite Micro Shock-W',
    category: 'glass-break',
    tagline: 'Wireless accelerometer shock sensor, Ricochet mesh, 3-yr CR2450 battery',
    verified: true,
    datasheetUrl: `${TEXECOM_PRODUCTS}premier-elite-micro-shock-w/`,
    specs: {
      method: 'piezo-shock', // datasheet calls it accelerometer, closest discriminated-union value
    },
  },

  // ═════════════════════════════════════════════════════════════════════
  // RISCO — control panels
  // ═════════════════════════════════════════════════════════════════════
  {
    id: 'risco-lightsys-plus',
    manufacturer: 'Risco',
    model: 'LightSYS+',
    category: 'alarm-panel',
    tagline: 'Hybrid panel — 8-512 zones, 32 partitions, 500 users, Grade 2/3',
    verified: true,
    datasheetUrl: RISCO_LIGHTSYS,
    specs: {
      series: 'LightSYS+',
      zonesOnboard: 8,
      zonesMax: 512,
      partitions: 32,
      outputsOnboard: 4,
      outputsMax: 196,
      users: 500,
      grade: 3,
      communicators: ['Ethernet', 'GSM-LTE'],
      mobileApp: 'iRISCO',
    },
  },
  {
    id: 'risco-lightsys-2',
    manufacturer: 'Risco',
    model: 'LightSYS 2',
    category: 'alarm-panel',
    tagline: 'Predecessor of LightSYS+ — hybrid wired/wireless, 50 zones max',
    verified: true,
    datasheetUrl: 'https://riscogroup.com/en/rscm_product/lightsys-2/',
    specs: {
      series: 'LightSYS',
      zonesOnboard: 8,
      zonesMax: 50,
      partitions: 4,
      users: 250,
      grade: 2,
      mobileApp: 'iRISCO',
    },
  },
  {
    id: 'risco-prosys-plus',
    manufacturer: 'Risco',
    model: 'ProSYS Plus',
    category: 'alarm-panel',
    tagline: 'Super-hybrid commercial — 64-512 zones, 32 partitions, 500 users, Grade 3',
    verified: true,
    datasheetUrl: RISCO_PROSYS_PLUS,
    specs: {
      series: 'ProSYS Plus',
      zonesOnboard: 8,
      zonesMax: 512,
      partitions: 32,
      outputsOnboard: 6,
      outputsMax: 262,
      users: 500,
      grade: 3,
      communicators: ['Ethernet', 'GSM-LTE'],
      mobileApp: 'iRISCO',
    },
  },

  // ═════════════════════════════════════════════════════════════════════
  // RISCO — keypads & detectors
  // ═════════════════════════════════════════════════════════════════════
  {
    id: 'risco-rp432kp',
    manufacturer: 'Risco',
    model: 'RP432KP',
    category: 'keypad',
    tagline: 'LightSYS/ProSYS LCD keypad, backlit, available black or white',
    verified: true,
    datasheetUrl: 'https://riscogroup.com/en/rscm_product/keypads/',
    specs: {
      display: 'lcd-text',
      connection: 'wired-risco-bus',
      compatibility: ['LightSYS+', 'LightSYS 2', 'ProSYS Plus'],
    },
  },
  {
    id: 'risco-rp432kpp',
    manufacturer: 'Risco',
    model: 'RP432KPP',
    category: 'keypad',
    tagline: 'LightSYS LCD keypad with built-in proximity reader',
    verified: true,
    datasheetUrl: 'https://riscogroup.com/en/rscm_product/keypads/',
    specs: {
      display: 'lcd-text',
      connection: 'wired-risco-bus',
      readerType: 'em-marin-125khz',
      compatibility: ['LightSYS+', 'LightSYS 2', 'ProSYS Plus'],
    },
  },
  {
    id: 'risco-watchout-dt-extreme',
    manufacturer: 'Risco',
    model: 'WatchOUT DT eXtreme',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    tagline: 'Outdoor dual-tech PIR + microwave, 15 m wide / 23 m long range, IP65',
    verified: true,
    datasheetUrl: RISCO_WATCHOUT,
    rangeM: 15,
    rangeStepsM: [15, 23], // wide-angle 90° vs long-range 5° barrier
    hfovDeg: 90,
    mountingHeightDefaultM: 2.5,
    mountingHeightMinM: 2.1,
    mountingHeightMaxM: 3.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir-mw-dual',
    antiMasking: true,
    petImmune: true,
    ipRating: 'IP65',
  },
  {
    id: 'risco-iwise-dt-am',
    manufacturer: 'Risco',
    model: 'iWISE DT AM',
    category: 'indoor-ceiling-pir',
    pattern: 'cone',
    tagline: 'Indoor PIR + microwave with anti-masking + anti-cloak (Grade 3)',
    verified: true,
    datasheetUrl: RISCO_IWISE,
    rangeM: 15,
    rangeStepsM: [10, 15, 25],
    hfovDeg: 90,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.1,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual',
    antiMasking: true,
  },
  {
    id: 'risco-iwise-quad-am',
    manufacturer: 'Risco',
    model: 'iWISE QUAD AM Grade 3',
    category: 'indoor-ceiling-pir',
    pattern: 'cone',
    tagline: 'Quad-element PIR with anti-masking, EN 50131 Grade 3 certified',
    verified: true,
    datasheetUrl: 'https://www.riscogroup.com/products/product/111',
    rangeM: 15,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.1,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
    antiMasking: true,
  },
];
